import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { cn } from '@/lib/core/utils/cn';
import { Button } from '@/components/ui/button';
import { ShopMiniCard, ShopMiniCardProps } from './mini-card';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SectionCarouselItem extends ShopMiniCardProps {
  id: string;
}

export interface SectionCarouselProps {
  title: string;
  /** Italic right-aligned subtitle — e.g. "NEW STYLES JUST LANDED". */
  subtitle?: string;
  buttonLabel: string;
  onButtonClick?: () => void;
  items: SectionCarouselItem[];
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SectionCarousel({
  title,
  subtitle,
  buttonLabel,
  onButtonClick,
  items,
  className,
}: SectionCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className={cn('flex w-full flex-col gap-6 overflow-hidden', className)}>
      {/* Title row */}
      <div className="flex items-center px-4">
        <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-[24px] leading-none font-semibold">
          {title}
        </span>
        {subtitle && (
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 shrink-0 text-right text-[16px] leading-none font-normal tracking-[0.25px] italic">
            {subtitle}
          </span>
        )}
      </div>

      {/* Carousel — bleeds to right edge; omitted when no items */}
      {items.length > 0 && (
        <div className="pl-4">
          <Swiper
            slidesPerView="auto"
            spaceBetween={8}
            onActiveIndexChange={swiper => setActiveIndex(swiper.activeIndex)}
            onReachEnd={() => setActiveIndex(Math.max(0, items.length - 1))}
          >
            {items.map(({ id, ...cardProps }) => (
              <SwiperSlide key={id} style={{ width: 'auto' }}>
                <ShopMiniCard {...cardProps} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Pagination dots */}
      {items.length > 1 && (
        <div className="flex items-center justify-center gap-2 px-4" aria-hidden="true">
          {items.map((_, i) => (
            <span
              key={i}
              className={cn(
                'block h-px w-4 transition-all duration-300',
                i === activeIndex ? 'bg-jncquoi-neutral-1000' : 'bg-jncquoi-neutral-1000/30'
              )}
            />
          ))}
        </div>
      )}

      {/* CTA button */}
      <div className="px-4">
        <Button
          variant="primary-solid"
          palette="dark"
          size="lg"
          className="w-full rounded-[2px]"
          onClick={onButtonClick}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
